// frontend/src/components/blog/BlogHomepage.jsx
// Fantasy basketball focused landing page — public, no auth required.
// Loads pre-built season averages from /nba-players.json (static, no rate limits).
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Trophy, Shield, Zap, Star, TrendingUp, Users,
  ChevronRight, Award, Target, Activity, Calculator,
} from 'lucide-react';
import ReferralStrip from '../shared/ReferralStrip';
import AdSlot from '../shared/AdSlot';

// ─── Current NBA season helper ─────────────────────────────────────────────────
function currentNbaSeason() {
  const now = new Date();
  const yr = now.getFullYear();
  return (now.getMonth() + 1) >= 10 ? yr : yr - 1;
}
function seasonLabel(s) { return `${s}-${String(s + 1).slice(2)}`; }

// ─── League distributions — empirically computed from 2024-25 season data ────
// Per-game: 46,150 rows, 509 players ≥10 games.  plus_minus: 291 players.
// oreb: offensive rebounds only (~38% of total reb on avg)
const LEAGUE = {
  pts:        { mean: 9.481,  stdDev: 6.513 },
  reb:        { mean: 3.777,  stdDev: 2.390 },
  oreb:       { mean: 0.900,  stdDev: 0.780 },  // offensive rebounds
  ast:        { mean: 2.194,  stdDev: 1.820 },
  stl:        { mean: 0.703,  stdDev: 0.404 },
  blk:        { mean: 0.424,  stdDev: 0.400 },
  fg3m:       { mean: 1.117,  stdDev: 0.894 },
  fg_pct:     { mean: 0.467,  stdDev: 0.091 },
  // plus_minus: per-game box score value over replacement proxy
  plus_minus: { mean: 0.176,  stdDev: 4.600 },
  // per-36 — role-normalised production for ROY scoring
  pts36:      { mean: 15.936, stdDev: 5.511 },
  ast36:      { mean: 3.643,  stdDev: 2.163 },
  reb36:      { mean: 5.670,  stdDev: 3.585 },
  oreb36:     { mean: 1.350,  stdDev: 1.170 },  // oreb per-36
  stl36:      { mean: 1.228,  stdDev: 0.561 },
  blk36:      { mean: 0.815,  stdDev: 0.739 },
  fg3m36:     { mean: 1.676,  stdDev: 1.341 },
};

// ─── Fantasy scoring formats ───────────────────────────────────────────────────
const FANTASY_FORMATS = {
  espn: {
    label: 'ESPN Standard',
    pts: 1, reb: 1.2, ast: 1.5, stl: 3, blk: 3, fg3m: 0, tov: -1,
    desc: 'PTS·1 · REB·1.2 · AST·1.5 · STL·3 · BLK·3 · TO·-1',
  },
  yahoo: {
    label: 'Yahoo',
    pts: 1, reb: 1.2, ast: 1.5, stl: 3, blk: 3, fg3m: 0.5, tov: -1.5,
    desc: 'PTS·1 · REB·1.2 · AST·1.5 · STL·3 · BLK·3 · 3PM·0.5 · TO·-1.5',
  },
  dk: {
    label: 'DraftKings',
    pts: 1, reb: 1.25, ast: 1.5, stl: 2, blk: 2, fg3m: 0.5, tov: -0.5,
    desc: 'PTS·1 · REB·1.25 · AST·1.5 · STL·2 · BLK·2 · 3PM·0.5 · TO·-0.5',
  },
  fanDuel: {
    label: 'FanDuel',
    pts: 1, reb: 1.2, ast: 1.5, stl: 2, blk: 2, fg3m: 0, tov: -1,
    desc: 'PTS·1 · REB·1.2 · AST·1.5 · STL·2 · BLK·2 · TO·-1',
  },
};

function fantasyScore(p, format) {
  const f = FANTASY_FORMATS[format];
  return (
    p.pts  * f.pts  +
    p.reb  * f.reb  +
    p.ast  * f.ast  +
    p.stl  * f.stl  +
    p.blk  * f.blk  +
    (p.fg3m || 0) * f.fg3m +
    (p.tov  || 0) * f.tov
  );
}

// ─── Current-season team win % ─────────────────────────────────────────────────
// BDL free tier doesn't expose standings. Updated Feb 2026 (2025-26 season).
const TEAM_WIN_PCT = {
  1:  0.380, // ATL
  2:  0.600, // BOS
  3:  0.250, // BKN
  4:  0.310, // CHA
  5:  0.440, // CHI
  6:  0.640, // CLE
  7:  0.490, // DAL
  8:  0.550, // DEN
  9:  0.450, // DET
  10: 0.410, // GSW
  11: 0.590, // HOU
  12: 0.530, // IND
  13: 0.480, // LAC
  14: 0.520, // LAL
  15: 0.280, // MEM
  16: 0.430, // MIA
  17: 0.400, // MIL
  18: 0.560, // MIN
  19: 0.230, // NOP
  20: 0.650, // NYK
  21: 0.780, // OKC
  22: 0.570, // ORL
  23: 0.270, // PHI
  24: 0.360, // PHX
  25: 0.260, // POR
  26: 0.500, // SAC
  27: 0.430, // SAS
  28: 0.520, // TOR
  29: 0.270, // UTA
  30: 0.190, // WAS
};

// ─── Rookie eligibility ───────────────────────────────────────────────────────
// We key rookie lists by NBA season start year so the right class is used.
// Season 2025 = 2025-26 → 2025 NBA Draft class.
// Season 2024 = 2024-25 → 2024 NBA Draft class.
const ROOKIE_NAMES_BY_SEASON = {
  2025: new Set([
    'Cooper Flagg', 'Dylan Harper', 'Ace Bailey', 'VJ Edgecombe', 'Tre Johnson',
    'Nolan Traore', 'Kon Knueppel', 'Kasparas Jakucionis', 'Egor Demin', 'Khaman Maluach',
    'Liam McNeeley', 'Labaron Philon', 'Rasheer Fleming', 'Collin Murray-Boyles',
    'Derik Queen', 'Maxime Raynaud', 'Drake Powell', 'Chaz Lanier', 'Walter Clayton Jr.',
    'Nique Clifford', 'Isaiah Crawford', 'Payton Sandfort', 'Isaiah Collier',
  ]),
  2024: new Set([
    'Zaccharie Risacher', 'Alex Sarr', 'Reed Sheppard', 'Donovan Clingan',
    'Dalton Knecht', 'Stephon Castle', 'Matas Buzelis', 'Rob Dillingham',
    'Bub Carrington', 'Yves Missi', 'Zach Edey', 'Cody Williams',
    'Kyle Filipowski', 'Tristan da Silva', 'Carlton Carrington',
    'Jaylen Wells', 'Devin Carter', 'Ryan Dunn', 'Pacome Dadiet',
    'Isaiah Collier', "Kel'el Ware", 'Jared McCain', 'Terrence Shannon Jr.',
    'Antonio Reeves', 'Johnny Furphy', 'Kevin McCullar Jr.',
    'Tidjane Salaun', "Ja'Kobe Walter", 'Nikola Topic',
  ]),
};
// Returns true only if the player was drafted in the season currently being shown
function isRookie(playerName) {
  const season = currentNbaSeason();
  const set = ROOKIE_NAMES_BY_SEASON[season];
  return set ? set.has(playerName) : false;
}

// ─── Math helpers ─────────────────────────────────────────────────────────────
function normCDF(z) {
  if (z < -8) return 0;
  if (z > 8)  return 100;
  const a1=0.254829592, a2=-0.284496736, a3=1.421413741,
        a4=-1.453152027, a5=1.061405429, p=0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - ((((a5*t+a4)*t+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
  return 50 * (1.0 + sign * y);
}

function pct(value, key) {
  const d = LEAGUE[key];
  if (!d || d.stdDev === 0) return 50;
  return normCDF((value - d.mean) / d.stdDev);
}

// ─── Award scoring formulas ───────────────────────────────────────────────────
// Off POY: PTS + AST primary; BPM carries heavier weight to signal true impact;
// offensive rebounds replace total rebounds; FG% dropped.
function offScore(p) {
  return (
    pct(p.pts,             'pts')        * 0.40 +
    pct(p.ast,             'ast')        * 0.25 +
    pct(p.plus_minus || 0, 'plus_minus') * 0.20 +
    pct(p.fg3m  || 0,      'fg3m')       * 0.10 +
    pct(p.oreb  || 0,      'oreb')       * 0.05
  );
}

// DPOY: DEF percentile 40%, BPM 20%, STL 20%, BLK 20%
// DEF percentile = composite of BPM + STL + BLK (same as defScore itself)
function defScore(p) {
  const defPercentile = (
    pct(p.plus_minus || 0, 'plus_minus') * 0.50 +
    pct(p.stl,             'stl')        * 0.25 +
    pct(p.blk,             'blk')        * 0.25
  );
  return (
    defPercentile                        * 0.40 +
    pct(p.plus_minus || 0, 'plus_minus') * 0.20 +
    pct(p.stl,             'stl')        * 0.20 +
    pct(p.blk,             'blk')        * 0.20
  );
}

// Per-game total percentile: average of key per-game percentiles
function perGameTotalScore(p) {
  return (
    pct(p.pts,             'pts')        * 0.35 +
    pct(p.ast,             'ast')        * 0.20 +
    pct(p.reb,             'reb')        * 0.20 +
    pct(p.stl,             'stl')        * 0.10 +
    pct(p.blk,             'blk')        * 0.10 +
    pct(p.fg_pct || 0,     'fg_pct')     * 0.05
  );
}

// MVP: 50% per-game total, 20% OFF, 10% DEF, 5% AllNBA, 5% Win%, 10% BPM
// Only players on 65+ game pace qualify.
function mvpScore(p) {
  const winPct = TEAM_WIN_PCT[p.team_id] ?? 0.500;
  return (
    perGameTotalScore(p)                 * 0.50 +
    offScore(p)                          * 0.20 +
    defScore(p)                          * 0.10 +
    allNbaScore(p)                       * 0.05 +
    winPct * 100                         * 0.05 +
    pct(p.plus_minus || 0, 'plus_minus') * 0.10
  );
}

// All-NBA: 50% per-game total, 30% OFF, 10% DEF, 10% VOL (pts percentile)
function allNbaScore(p) {
  return (
    perGameTotalScore(p)                 * 0.50 +
    offScore(p)                          * 0.30 +
    defScore(p)                          * 0.10 +
    pct(p.pts, 'pts')                    * 0.10
  );
}

// ROY: 70% per-game, 30% per-36. Each half blends offensive AND defensive
// percentiles so two-way rookies are properly rewarded.
function rookiePerGameScore(p) {
  // off: PTS, AST, BPM, oreb, fg3m  |  def: BPM, STL, BLK
  const offPG = (
    pct(p.pts,             'pts')        * 0.40 +
    pct(p.ast,             'ast')        * 0.25 +
    pct(p.plus_minus || 0, 'plus_minus') * 0.20 +
    pct(p.fg3m  || 0,      'fg3m')       * 0.10 +
    pct(p.oreb  || 0,      'oreb')       * 0.05
  );
  const defPG = (
    pct(p.plus_minus || 0, 'plus_minus') * 0.50 +
    pct(p.stl,             'stl')        * 0.25 +
    pct(p.blk,             'blk')        * 0.25
  );
  return offPG * 0.60 + defPG * 0.40;
}
function rookiePer36Score(p) {
  const mpg = p.mpg || 1;
  const s36 = Math.min(36 / mpg, 4); // cap scale at 4× to avoid bench-warmer inflation
  const offP36 = (
    pct(p.pts  * s36,             'pts36')  * 0.40 +
    pct(p.ast  * s36,             'ast36')  * 0.25 +
    pct(p.plus_minus || 0,        'plus_minus') * 0.20 + // BPM doesn't scale with mins
    pct((p.fg3m || 0) * s36,      'fg3m36') * 0.10 +
    pct((p.oreb || 0) * s36,      'oreb36') * 0.05
  );
  const defP36 = (
    pct(p.plus_minus || 0,        'plus_minus') * 0.50 +
    pct(p.stl  * s36,             'stl36')  * 0.25 +
    pct(p.blk  * s36,             'blk36')  * 0.25
  );
  return offP36 * 0.60 + defP36 * 0.40;
}
function rookieScore(p) {
  return rookiePerGameScore(p) * 0.80 + rookiePer36Score(p) * 0.20;
}

// ─── Data load ────────────────────────────────────────────────────────────────
// season_averages endpoint only works for COMPLETED seasons.
// Strategy: try the pre-baked static JSON first (instant load, no API calls).
// If absent, stale (>24 h), or empty, fall back to live /stats pagination.
const STATIC_JSON_URL = '/nba-players.json';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

async function loadPlayers() {
  try {
    const res = await fetch(STATIC_JSON_URL, { cache: 'no-store' });
    if (res.ok) {
      const json = await res.json();
      const players = json.players || [];
      const season  = json.season;
      const generated = json.generated ? new Date(json.generated) : null;
      const age = generated ? Date.now() - generated.getTime() : Infinity;
      if (
        players.length > 0 &&
        season === currentNbaSeason() &&
        age < MAX_AGE_MS
      ) {
        return players;
      }
    }
  } catch (_) { /* fall through */ }
  // Static JSON missing, wrong season, or stale — fetch live
  return fetchLiveStats();
}

const BDL_KEY = 'dc491062-9c3f-4f01-9d86-84c31e36e9d5';

async function bdlFetch(url) {
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${BDL_KEY}` },
    });
    if (res.status === 429) {
      await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
      continue;
    }
    return res;
  }
  return null;
}

async function fetchLiveStats() {
  const season = currentNbaSeason();
  const playerMap = {};
  let cursor = null;
  let page = 0;
  const MAX_PAGES = 200; // up to 20,000 rows — full current season

  while (page < MAX_PAGES) {
    let url = `https://api.balldontlie.io/nba/v1/stats?seasons[]=${season}&per_page=100`;
    if (cursor) url += `&cursor=${cursor}`;

    const res = await bdlFetch(url);
    if (!res || !res.ok) break;

    const data = await res.json();
    const rows = data.data || [];
    if (!rows.length) break;

    for (const row of rows) {
      const mins = parseInt(row.min || '0', 10) || 0;
      if (mins < 1) continue;
      const pid = row.player?.id;
      if (!pid) continue;

      if (!playerMap[pid]) {
        playerMap[pid] = {
          id: pid,
          first_name: row.player?.first_name || '',
          last_name:  row.player?.last_name  || '',
          team_id:    row.team?.id,
          team_abbr:  row.team?.abbreviation || '—',
          games: 0, pts: 0, reb: 0, oreb: 0, ast: 0, stl: 0, blk: 0,
          fg3m: 0, fgm: 0, fga: 0, tov: 0, min_total: 0, pm: 0,
        };
      }
      const p = playerMap[pid];
      p.games     += 1;
      p.pts       += Number(row.pts)          || 0;
      p.reb       += Number(row.reb)          || 0;
      p.oreb      += Number(row.oreb)         || 0;
      p.ast       += Number(row.ast)          || 0;
      p.stl       += Number(row.stl)          || 0;
      p.blk       += Number(row.blk)          || 0;
      p.fg3m      += Number(row.fg3m)         || 0;
      p.fgm       += Number(row.fgm)          || 0;
      p.fga       += Number(row.fga)          || 0;
      p.tov       += Number(row.turnover)     || 0;
      p.min_total += mins;
      p.pm        += Number(row.plus_minus)   || 0;
    }

    cursor = data.meta?.next_cursor;
    page++;
    if (!cursor) break;
  }

  return Object.values(playerMap)
    .filter((p) => p.games >= 10)
    .map((p) => ({
      ...p,
      pts:         p.pts       / p.games,
      reb:         p.reb       / p.games,
      oreb:        p.oreb      / p.games,
      ast:         p.ast       / p.games,
      stl:         p.stl       / p.games,
      blk:         p.blk       / p.games,
      fg3m:        p.fg3m      / p.games,
      tov:         p.tov       / p.games,
      fg_pct:      p.fga > 0 ? p.fgm / p.fga : 0,
      plus_minus:  p.pm        / p.games,
      mpg:         p.min_total / p.games,
    }));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RankBadge({ rank }) {
  if (rank === 1) return <span className="text-yellow-400 font-black text-lg w-6 text-center">1</span>;
  if (rank === 2) return <span className="text-gray-300 font-black text-lg w-6 text-center">2</span>;
  if (rank === 3) return <span className="text-orange-400 font-black text-lg w-6 text-center">3</span>;
  return <span className="text-gray-500 font-bold w-6 text-center">{rank}</span>;
}

function StatPill({ label, value, highlight }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
      highlight ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-gray-700/60'
    }`}>
      <span className={highlight ? 'text-orange-400' : 'text-gray-500'}>{label}</span>
      <span className={`font-semibold ${highlight ? 'text-orange-300' : 'text-gray-200'}`}>{value}</span>
    </span>
  );
}

function PlayerRow({ rank, player, score, scoreLabel, badge, onAnalyze, highlightStat }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-gray-700/30 ${rank <= 3 ? 'bg-gray-800/40' : ''}`}>
      <div className="flex-shrink-0 w-6 text-center">
        <RankBadge rank={rank} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold text-sm">
            {player.first_name} {player.last_name}
            {player.projected65 === false && <span className="text-yellow-400 ml-1">*</span>}
          </span>
          <span className="text-gray-500 text-xs bg-gray-700/50 px-1.5 py-0.5 rounded">{player.team_abbr}</span>
          {badge}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <StatPill label="PTS" value={player.pts.toFixed(1)} highlight={highlightStat === 'pts'} />
          <StatPill label="REB" value={player.reb.toFixed(1)} highlight={highlightStat === 'reb'} />
          <StatPill label="AST" value={player.ast.toFixed(1)} highlight={highlightStat === 'ast'} />
          <StatPill label="STL" value={player.stl.toFixed(1)} highlight={highlightStat === 'stl'} />
          <StatPill label="BLK" value={player.blk.toFixed(1)} highlight={highlightStat === 'blk'} />
          <StatPill label="GP"  value={player.games} />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right">
          <div className="text-orange-400 font-black text-sm">{score.toFixed(1)}</div>
          <div className="text-gray-600 text-xs">{scoreLabel}</div>
        </div>
        <button
          onClick={() => onAnalyze(player)}
          className="p-1.5 rounded-lg bg-gray-700/40 hover:bg-orange-500/20 hover:text-orange-400 text-gray-500 transition-colors"
          title="Open player deep dive"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function RankingPanel({ players, scoreKey, scoreLabel, loading, onAnalyze, badge, highlightStat }) {
  return (
    <div className="bg-gray-800/40 rounded-2xl border border-gray-700/40 overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading season stats…</p>
          </div>
        </div>
      ) : players.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-10">No data available</p>
      ) : (
        <div className="divide-y divide-gray-700/30">
          {players.map((p, i) => (
            <PlayerRow
              key={p.id}
              rank={i + 1}
              player={p}
              score={p[scoreKey]}
              scoreLabel={scoreLabel}
              badge={badge?.(p)}
              onAnalyze={onAnalyze}
              highlightStat={highlightStat}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AllNBAPanel({ teams, loading, onAnalyze }) {
  const labels  = ['1st Team', '2nd Team', '3rd Team'];
  const colors  = ['text-yellow-400', 'text-gray-300', 'text-orange-400'];
  const borders = ['border-yellow-500/20', 'border-gray-600/30', 'border-orange-500/20'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {teams.map((team, ti) => (
        <div key={ti} className={`bg-gray-800/40 rounded-2xl border ${borders[ti]} overflow-hidden`}>
          <div className={`px-4 py-3 border-b border-gray-700/40 ${colors[ti]} font-bold text-sm`}>
            {labels[ti]}
          </div>
          <div className="divide-y divide-gray-700/30">
            {team.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700/20 transition-colors">
                <span className={`font-black text-sm ${colors[ti]} w-4 flex-shrink-0`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">
                    {p.first_name} {p.last_name}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {p.team_abbr} · {p.pts.toFixed(1)}pts {p.reb.toFixed(1)}reb {p.ast.toFixed(1)}ast
                  </div>
                </div>
                <button
                  onClick={() => onAnalyze(p)}
                  className="p-1 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-orange-400 transition-colors flex-shrink-0"
                  title="Open player deep dive"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FantasyPanel({ players, loading, onAnalyze, format, setFormat }) {
  const formatKeys = Object.keys(FANTASY_FORMATS);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  const ranked = [...players]
    .map((p) => ({ ...p, fpts: fantasyScore(p, format) }))
    .sort((a, b) => b.fpts - a.fpts)
    .slice(0, 25);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide mr-1">Scoring:</span>
        {formatKeys.map((key) => (
          <button
            key={key}
            onClick={() => setFormat(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              format === key
                ? 'bg-orange-500 text-white shadow shadow-orange-500/30'
                : 'bg-gray-800/60 text-gray-400 hover:text-white border border-gray-700/50'
            }`}
          >
            {FANTASY_FORMATS[key].label}
          </button>
        ))}
      </div>
      <p className="text-gray-600 text-xs mb-4">{FANTASY_FORMATS[format].desc}</p>

      <div className="bg-gray-800/40 rounded-2xl border border-gray-700/40 overflow-hidden">
        <div className="grid grid-cols-[2rem_1fr_3rem_3rem_3rem_3rem_3rem_3.5rem_4rem_2rem] gap-1 items-center px-3 py-2 border-b border-gray-700/50 text-gray-600 text-xs font-semibold uppercase tracking-wide">
          <span>#</span>
          <span>Player</span>
          <span className="text-right">PTS</span>
          <span className="text-right">REB</span>
          <span className="text-right">AST</span>
          <span className="text-right">STL</span>
          <span className="text-right">BLK</span>
          <span className="text-right">3PM</span>
          <span className="text-right text-orange-400">FPTS</span>
          <span />
        </div>
        <div className="divide-y divide-gray-700/30">
          {ranked.map((p, i) => (
            <div
              key={p.id}
              className={`grid grid-cols-[2rem_1fr_3rem_3rem_3rem_3rem_3rem_3.5rem_4rem_2rem] gap-1 items-center px-3 py-2.5 hover:bg-gray-700/20 transition-colors text-xs ${i < 3 ? 'bg-gray-800/40' : ''}`}
            >
              <RankBadge rank={i + 1} />
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{p.first_name} {p.last_name}</div>
                <div className="text-gray-600 text-xs">{p.team_abbr} · {p.games}g</div>
              </div>
              <span className="text-gray-300 text-right tabular-nums">{p.pts.toFixed(1)}</span>
              <span className="text-gray-300 text-right tabular-nums">{p.reb.toFixed(1)}</span>
              <span className="text-gray-300 text-right tabular-nums">{p.ast.toFixed(1)}</span>
              <span className="text-gray-300 text-right tabular-nums">{p.stl.toFixed(1)}</span>
              <span className="text-gray-300 text-right tabular-nums">{p.blk.toFixed(1)}</span>
              <span className="text-gray-300 text-right tabular-nums">{(p.fg3m || 0).toFixed(1)}</span>
              <span className="text-orange-400 font-black text-right tabular-nums">{p.fpts.toFixed(1)}</span>
              <button
                onClick={() => onAnalyze(p)}
                className="p-1 rounded hover:bg-gray-700 text-gray-600 hover:text-orange-400 transition-colors"
                title="Open player deep dive"
              >
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function toPlayerSlug(firstName, lastName) {
  return `${firstName}-${lastName}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function BlogHomepage() {
  const navigate = useNavigate();
  const [players, setPlayers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [source, setSource]     = useState('');
  const [activeTab, setActiveTab]         = useState('mvp');
  const [fantasyFormat, setFantasyFormat] = useState('espn');

  useEffect(() => {
    loadPlayers()
      .then((data) => {
        setPlayers(data);
        setSource(data.length > 0 ? 'loaded' : 'empty');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const scored = useMemo(() => players.map((p) => ({
    ...p,
    offScore:         offScore(p),
    defScore:         defScore(p),
    mvpScore:         mvpScore(p),
    allNbaScore:      allNbaScore(p),
    rookieScore:      rookieScore(p),
    rookiePg:         rookiePerGameScore(p),
    rookiePer36:      rookiePer36Score(p),
  })), [players]);

  // Helper: calculate 65-game projection for asterisk
  const add65Projection = useMemo(() => {
    const now = new Date();
    const seasonStart = new Date(now.getFullYear() - (now.getMonth() < 9 ? 1 : 0), 9, 22); // Oct 22
    const seasonEnd   = new Date(seasonStart.getFullYear() + 1, 3, 13);                     // Apr 13
    const totalDays   = (seasonEnd - seasonStart) / 86400000;
    const elapsed     = Math.max((now - seasonStart) / 86400000, 1);
    const fraction    = Math.min(elapsed / totalDays, 1);

    return (player) => {
      const projected = fraction > 0 ? player.games / fraction : player.games;
      return { ...player, projected65: projected >= 65 };
    };
  }, []);

  const top = (key, n = 10) =>
    [...scored].map(add65Projection).sort((a, b) => b[key] - a[key]).slice(0, n);

  // MVP: all players ranked, asterisk if projected <65 games.
  const topMvp = useMemo(() =>
    scored.map(add65Projection).sort((a, b) => b.mvpScore - a.mvpScore).slice(0, 10),
    [scored, add65Projection]
  ); // eslint-disable-line

  const topOff    = useMemo(() => top('offScore'),       [scored, add65Projection]); // eslint-disable-line
  const topDef    = useMemo(() => top('defScore'),       [scored, add65Projection]); // eslint-disable-line
  const topAllNba = useMemo(() => top('allNbaScore', 15),[scored, add65Projection]); // eslint-disable-line
  const allNbaTeams = [topAllNba.slice(0, 5), topAllNba.slice(5, 10), topAllNba.slice(10, 15)];

  const topRookies = useMemo(() => {
    const fullName = (p) => `${p.first_name} ${p.last_name}`;
    return scored
      .filter((p) => isRookie(fullName(p)))
      .map(add65Projection)
      .sort((a, b) => b.rookieScore - a.rookieScore)
      .slice(0, 10);
  }, [scored, add65Projection]);

  const handleAnalyze = (player) => {
    navigate(`/app/${toPlayerSlug(player.first_name, player.last_name)}`);
  };

  const season = currentNbaSeason();

  const TABS = [
    { id: 'mvp',     label: 'MVP Race',    icon: Trophy,     desc: 'Per-Game 50% · OFF 20% · DEF 10% · AllNBA 5% · Win% 5% · BPM 10% · * = <65 game projection' },
    { id: 'off',     label: 'Off POY',     icon: Zap,        desc: 'PTS 40% · AST 25% · BPM 20% · 3PM 10% · OREB 5%' },
    { id: 'def',     label: 'Def POY',     icon: Shield,     desc: 'DEF 40% · BPM 20% · STL 20% · BLK 20%' },
    { id: 'allnba',  label: 'All-NBA',     icon: Award,      desc: 'Per-Game 50% · OFF 30% · DEF 10% · VOL 10%' },
    { id: 'rookie',  label: 'ROY',         icon: Star,       desc: 'Per-Game 80% · Per-36 20% · OFF+DEF (draft class only)' },
    { id: 'fantasy', label: 'Fantasy Pts', icon: Calculator, desc: FANTASY_FORMATS[fantasyFormat].desc },
  ];

  const activeTabMeta = TABS.find((t) => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">

      {/* Header */}
      <header className="bg-gray-900/50 backdrop-blur-xl border-b border-gray-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-orange-500 mr-3" aria-hidden="true" />
            <span className="text-2xl font-black text-white">
              Hoops<span className="text-orange-500">Analyst</span>
            </span>
          </div>
          <a
            href="/app"
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
          >
            <Activity className="h-4 w-4" aria-hidden="true" />
            Player Deep Dive
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-10 pb-16">

        {/* Hero */}
        <section aria-label="NBA rankings hero" className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 text-orange-400 text-xs font-bold uppercase tracking-wider mb-4">
            <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
            {seasonLabel(season)} · Live Season Projections
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
            NBA Fantasy Basketball<br />
            <span className="text-orange-500">Rankings &amp; Award Predictions</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
            Data-driven {seasonLabel(season)} NBA rankings for fantasy basketball leagues on DraftKings, FanDuel, ESPN, and Yahoo.
            Track the live MVP race, Rookie of the Year, Defensive Player of the Year, and All-NBA team projections —
            all scored against real league-wide percentiles so you always know who the top players are.
          </p>
        </section>

        {/* Stat bar */}
        <div className="grid grid-cols-3 gap-3 mb-8 max-w-md mx-auto">
          {[
            { label: 'Players Tracked', value: loading ? '…' : `${scored.length}`, icon: Users },
            { label: 'Award Categories', value: '5',                               icon: Trophy },
            { label: 'Season',           value: seasonLabel(season),               icon: Activity },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 text-center">
              <Icon className="h-4 w-4 text-orange-500 mx-auto mb-1" />
              <div className="text-white font-bold">{value}</div>
              <div className="text-gray-600 text-xs">{label}</div>
            </div>
          ))}
        </div>

        {/* Partner referral strip — update href values in src/config/referrals.js */}
        <ReferralStrip variant="strip" className="mb-8" />

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm text-center mb-6">
            Failed to load stats: {error}
          </div>
        )}

        {/* Tab nav */}
        <div className="flex gap-2 flex-wrap justify-center mb-2">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === id
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                  : 'bg-gray-800/60 text-gray-400 hover:text-white border border-gray-700/50 hover:border-gray-600'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <p className="text-center text-gray-600 text-xs mb-5">
          {activeTab === 'fantasy'
            ? FANTASY_FORMATS[fantasyFormat].desc
            : `Scoring weights: ${activeTabMeta?.desc}`}
        </p>

        {/* Content */}
        {activeTab === 'mvp' && (
          <RankingPanel
            players={topMvp}
            scoreKey="mvpScore"
            scoreLabel="MVP score"
            loading={loading}
            onAnalyze={handleAnalyze}
            badge={(p) => (
              <span className="text-xs text-gray-600 tabular-nums">
                {((TEAM_WIN_PCT[p.team_id] ?? 0.5) * 100).toFixed(0)}% W
              </span>
            )}
          />
        )}

        {activeTab === 'off' && (
          <RankingPanel
            players={topOff}
            scoreKey="offScore"
            scoreLabel="Off score"
            loading={loading}
            onAnalyze={handleAnalyze}
            highlightStat="pts"
            badge={(p) => (
              <span className="text-xs text-gray-600">
                {(p.fg3m || 0).toFixed(1)} 3PM · {((p.fg_pct || 0) * 100).toFixed(1)}% FG
              </span>
            )}
          />
        )}

        {activeTab === 'def' && (
          <RankingPanel
            players={topDef}
            scoreKey="defScore"
            scoreLabel="Def score"
            loading={loading}
            onAnalyze={handleAnalyze}
            highlightStat="stl"
          />
        )}

        {activeTab === 'allnba' && (
          <AllNBAPanel
            teams={allNbaTeams}
            loading={loading}
            onAnalyze={handleAnalyze}
          />
        )}

        {activeTab === 'rookie' && (
          topRookies.length === 0 && !loading ? (
            <div className="bg-gray-800/40 rounded-2xl border border-green-500/20 p-10 text-center">
              <Star className="h-10 w-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm max-w-sm mx-auto">
                No known rookies found in the current dataset. Check back as the season progresses.
              </p>
            </div>
          ) : (
            <RankingPanel
              players={topRookies}
              scoreKey="rookieScore"
              scoreLabel="ROY score"
              loading={loading}
              onAnalyze={handleAnalyze}
            />
          )
        )}

        {activeTab === 'fantasy' && (
          <FantasyPanel
            players={scored}
            loading={loading}
            onAnalyze={handleAnalyze}
            format={fantasyFormat}
            setFormat={setFantasyFormat}
          />
        )}

        {/* Ad slot — leaderboard below rankings; only renders when configured in referrals.js */}
        <AdSlot slot="leaderboard" className="mt-8" />

        {/* Partner cards — renders when referral hrefs are set in referrals.js */}
        <ReferralStrip variant="cards" className="mt-8" />

        {/* CTA */}
        <div className="mt-10 bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-2xl p-8 text-center">
          <Target className="h-8 w-8 text-orange-500 mx-auto mb-3" />
          <h3 className="text-white font-bold text-lg mb-2">Full Player Analytics</h3>
          <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
            Game logs · Scatter plot trends · Bell curve distributions · Player profile quadrant ·
            Per-36 percentile rankings — all in one place for any NBA player.
          </p>
          <a
            href="/app"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-bold transition-colors text-sm"
          >
            Open Analytics Tool
            <ChevronRight className="h-4 w-4" />
          </a>
        </div>

        {source && !loading && (
          <p className="text-center text-gray-800 text-xs mt-4">
            Data via BallDontLie API · {seasonLabel(season)} season averages
          </p>
        )}
      </main>

      <footer className="border-t border-gray-800/60 py-6 text-center text-gray-700 text-xs">
        <p>
          HoopsAnalyst · NBA Fantasy Basketball Analytics · {seasonLabel(season)} Season
        </p>
        <p className="mt-1">
          Rankings are percentile-based projections, not official NBA award selections.
        </p>
        <nav aria-label="Footer links" className="mt-2 flex justify-center gap-4">
          <a href="/" className="hover:text-gray-500 transition-colors">Rankings</a>
          <a href="/app" className="hover:text-gray-500 transition-colors">Player Deep Dive</a>
        </nav>
      </footer>
    </div>
  );
}
